import { Navigate } from "react-router-dom";
import { useAtomValue } from "jotai";
import { Button, Spinner, Title } from "@common/index";
import { ComponentColor } from "@common/types";
import { ROUTES } from "@routing/routes";
import { currentUserAtom, userFetchErrorAtom } from "@/store/userAtoms";
import { AccountLoadError } from "@components/Auth/AccountLoadError";
import { ApprovalStatus } from "@models/user";
import { RejectedLocations } from "./_components/RejectedLocations";

// Pre-approval wall landing page (E5-S6). Reached when a signed-in but
// not-yet-approved user hits any gated `/c` route. Design ref: deliverable #266.
export const PendingPage = () => {
    const user = useAtomValue(currentUserAtom);
    const hasFetchError = useAtomValue(userFetchErrorAtom);

    // Loading window — currentUserAtom is null until getMeUser() resolves.
    // A failed read is its own state, not a spinner that never ends.
    if (!user) {
        if (hasFetchError) {
            return <AccountLoadError />;
        }

        return (
            <div className="flex justify-center py-20">
                <Spinner size="lg" />
            </div>
        );
    }

    // An approved user should never see the wall — send them to the app.
    if (user.approvalStatus === ApprovalStatus.Approved) {
        return <Navigate to={ROUTES.BROWSE} replace />;
    }

    const isRejected = user.approvalStatus === ApprovalStatus.Rejected;

    return (
        <div className="max-w-2xl mx-auto px-4 py-10 text-primary">
            {isRejected ? (
                <>
                    <Title>Application Not Approved</Title>
                    {/* E5-S11 (#198): resubmission is per-location and in
                        place — the old link back to /apply 409s on the email
                        dedup under the per-location model. */}
                    <RejectedLocations
                        fallbackReason={user.rejectionReason}
                    />
                </>
            ) : (
                <>
                    <Title>Application Under Review</Title>
                    <p className="text-base mt-6">
                        Your application is under review. We will notify you once
                        it is approved.
                    </p>
                    <div className="mt-8">
                        <Button
                            to={ROUTES.SUPPORT}
                            color={ComponentColor.Primary}
                            className="normal-case"
                        >
                            Contact Support
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};
